import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ChevronLeft, Trash2, Plus } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function MyConstructions() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/constructions");
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    try {
      await api.delete(`/constructions/${id}`);
      setItems((prev) => prev.filter((x) => x.id !== id));
      toast.success("Проект удалён");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="min-h-screen bg-cream flex flex-col" data-testid="projects-page">
      <header className="flex items-center gap-3 px-5 pt-5 pb-3">
        <button
          onClick={() => nav("/account")}
          data-testid="back-to-architect-button"
          className="p-2 -ml-2 rounded-full hover:bg-sand/50"
          aria-label="Назад"
        >
          <ChevronLeft className="w-5 h-5 text-espresso" />
        </button>
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-[0.28em] text-muted-foreground">
            {user?.email}
          </div>
          <h1 className="font-serif text-2xl text-espresso -mt-0.5">Мои проекты</h1>
        </div>
        <Button
          onClick={() => nav("/architect")}
          className="btn-pill bg-espresso hover:bg-espressoDark text-white text-sm h-auto py-2 px-4"
          data-testid="new-project-button"
        >
          <Plus className="w-4 h-4 mr-1" /> Новый
        </Button>
      </header>

      <div className="px-5 pb-8 flex-1">
        {loading ? (
          <p className="text-center text-sm text-muted-foreground py-10 font-serif italic">
            Загружаем архив…
          </p>
        ) : items.length === 0 ? (
          <div className="text-center mt-16 px-6">
            <p className="font-serif text-xl text-espresso/80">Пока нет сохранённых проектов</p>
            <p className="text-sm text-muted-foreground mt-2">
              Соберите свой первый десерт — и он появится здесь.
            </p>
            <Button
              onClick={() => nav("/architect")}
              className="btn-pill bg-espresso hover:bg-espressoDark text-white mt-6"
              data-testid="empty-create-button"
            >
              Начать проект
            </Button>
          </div>
        ) : (
          <ul className="space-y-3 mt-2" data-testid="projects-list">
            {items.map((c) => (
              <li
                key={c.id}
                data-testid={`project-item-${c.id}`}
                className="rounded-2xl bg-white border hairline p-4 flex items-center gap-3 hover:border-gold transition-colors"
              >
                <button
                  onClick={() => nav(`/architect/${c.id}`)}
                  className="flex-1 text-left"
                  data-testid={`open-project-${c.id}`}
                >
                  <div className="font-serif text-lg text-espresso">{c.name}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {c.layers.length} слоёв · {c.total_thickness_cm.toFixed(1)} см
                  </div>
                </button>
                <div className="flex -space-x-1">
                  {c.layers.slice(-4).map((l, i) => (
                    <span
                      key={l.id + i}
                      className="w-6 h-6 rounded-full border-2 border-white"
                      style={{ background: l.color }}
                    />
                  ))}
                </div>
                <button
                  onClick={() => remove(c.id)}
                  data-testid={`delete-project-${c.id}`}
                  className="p-2 rounded-full hover:bg-red-50 transition-colors ml-1"
                  aria-label="Удалить"
                >
                  <Trash2 className="w-4 h-4 text-red-500" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
